import { sanitize } from '@picsart/growth-rc/old/SanitizeHtml';

import { useTranslator } from '@picsart/rc/services/localization';

import type { ItemProps } from 'redesignedComponents/sections/HowTo/types';

import { TemplateModel } from 'templates/types';
import buildSlugFromNameAndId from 'utils/buildSlugFromNameAndId';
import trim from 'utils/trim';
import { BreadcrumbSchemaDataModel, JSONLinkedDataProps, SchemaType } from './types';

const contextProp = '@context';
const typeProp = '@type';
const graphProp = '@graph';
const idProp = '@id';
const mainUrl = window.location.origin;
const schemaUrl = 'https://schema.org';
const getOriginUrlWithPathName = (pathname: string) => trim(`${mainUrl}${pathname}`, '/');

const schemaFaq = {
  [contextProp]: schemaUrl,
  [typeProp]: 'FAQPage',
};

const howToSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'HowTo',
};

const ratingSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'SoftwareApplication',
};
const webPageSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'WebSite',
};
const reviewSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'Review',
};

const productSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'Product',
};

const articleSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'Article',
};

const itemListSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'ItemList',
};
const videoObjectSchema = {
  [contextProp]: schemaUrl,
  [typeProp]: 'VideoObject',
};

export const useFAQSchema = (data: Record<string, any>) => {
  const translate = useTranslator();

  const mainEntity = data.items.map(({ question, answer }) => ({
    [typeProp]: 'Question',
    name: sanitize(translate(question), { ALLOWED_TAGS: [] }),
    acceptedAnswer: {
      [typeProp]: 'Answer',
      text: sanitize(translate(answer), { ALLOWED_TAGS: [] }),
    },
  }));
  return {
    ...schemaFaq,
    [idProp]: `${getOriginUrlWithPathName(data.pathname)}/#faq`,
    mainEntity,
  };
};

export const useHowToSchema = (data: Record<string, any>) => {
  const translate = useTranslator();
  // this type unique and we used only there, this types used in google scheme,
  // todo refactor to enum
  const step = data?.order?.map((item, index) => ({
    [typeProp]: 'HowToStep',
    name: `Step ${index + 1}`,
    text: sanitize(translate(item), { ALLOWED_TAGS: [] }),
  }));
  return {
    ...howToSchema,
    name: data?.title,
    [idProp]: data[idProp] || `${getOriginUrlWithPathName(data.pathname)}/#howto`,
    step,
  };
};

export const useRedesignedHowToSchema = (data: Record<string, any>) => {
  return useHowToSchema({
    title: data.title.text,
    [idProp]: `${getOriginUrlWithPathName(data.pathname)}/#howto`,
    order: data.listItems.map(({ description }: ItemProps) => description),
  });
};

export const useOrganizationSchema = () => {
  return {
    [contextProp]: schemaUrl,
    [graphProp]: [
      {
        [typeProp]: 'Organization',
        [idProp]: `${mainUrl}/#organization`,
        name: 'Picsart',
        logo: 'https://yearly-cdn.picsart.com/cdn140/20187637208797289426.png',
        url: mainUrl,
        sameAs: [
          'https://www.facebook.com/picsart',
          'https://twitter.com/picsart',
          'https://www.instagram.com/picsart',
          'https://www.pinterest.com/picsart',
          'https://www.linkedin.com/company/picsart-photo-studio',
          'https://www.youtube.com/picsart',
        ],
        address: {
          [typeProp]: 'PostalAddress',
          streetAddress: '429 Lenox Ave Space',
          addressLocality: 'Miami',
          addressRegion: 'Florida',
          postalCode: '33139',
          addressCountry: 'United States',
        },
      },
    ],
  };
};
export const useWebPageSchema = (data: Record<string, any>) => {
  return {
    ...webPageSchema,
    [idProp]: data.url ? `${mainUrl}/${data.url}/#website` : `${mainUrl}/#website`,
    name: data.name,
    description: data.description,
    url: data.url ? `${mainUrl}/${data.url}` : mainUrl,
  };
};
export const useProductSchema = (data: Record<string, any>) => {
  return {
    ...productSchema,
    [idProp]: `${mainUrl}/${data.url}/#product`,
    name: data.name,
    description: data.description,
    image: data.image,
    brand: {
      [idProp]: `${mainUrl}/#organization`,
    },
    offers: {
      [typeProp]: 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };
};
export const useReviewSchema = (data: Record<string, any>) => {
  return {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      bestRating: '5',
      reviewCount: '18',
    },
    offers: {
      [typeProp]: 'Offer',
      availability: 'https://schema.org/InStock ',
    },
    review: [
      ...data.map(item => ({
        ...reviewSchema,
        itemReviewed: {
          [typeProp]: 'SoftwareApplication',
          name: 'Picsart: Editing and Design Tools',
          operatingSystem: ['Windows', 'OS', 'Android'],
          applicationCategory: 'DesignApplication',
        },
        author: {
          [typeProp]: 'Person',
          name: item.name,
        },
        datePublished: item.date,
        reviewRating: {
          [typeProp]: 'Rating',
          ratingValue: item.rating,
        },
        reviewBody: item.content,
      })),
    ],
  };
};

export const useRatingSchema = (data: Record<string, any>) => {
  const getLastPathSegment = (pathname: string) => {
    if (!pathname) return '';
    const cleanPath = trim(pathname, '/');
    const segments = cleanPath.split('/');
    return segments[segments.length - 1] || pathname;
  };
  const mainEntity = {
    name: getLastPathSegment(data.pathname),
    description: data.description,
    [idProp]: `${getOriginUrlWithPathName(data.pathname)}/#softwareapplication`,
    operatingSystem: 'Mac, Windows, IOS, Android',
    applicationCategory: 'BrowserApplication',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingCount: data.ratingCount,
      ratingValue: data.ratingValue,
    },
    url: `${mainUrl}/apps`,
    downloadUrl: [
      'https://play.google.com/store/apps/details?id=com.picsart.studio&hl=en&gl=US',
      'https://apps.apple.com/us/app/picsart-ai-photo-editor/id587366035',
    ],
  };
  return { ...ratingSchema, ...mainEntity };
};

export const useBreadcrumbsSchema = (data: BreadcrumbSchemaDataModel[]) => {
  const mainEntity = {
    [contextProp]: schemaUrl,
    [typeProp]: 'BreadcrumbList',
    itemListElement: data.map(item => ({
      [typeProp]: 'ListItem',
      position: item.position,
      name: item.anchor,
      ...(item.link ? { item: `${mainUrl}${item.link}` } : {}),
    })),
  };

  return mainEntity;
};

export const useArticleSchema = (data: Record<string, any>) => {
  return {
    ...articleSchema,
    headline: data?.seoSettings?.title,
    description: data?.seoSettings?.metaDescription,
    image: data?.imgSrc,
    author: {
      [typeProp]: 'Organization',
      name: 'Picsart',
      url: 'https://picsart.com',
    },
    datePublished:
      data?.colorPages?.find(item => item.slug === data.slug)?.createdAt ||
      (data?.hubs?.length && data.hubs[0].publishedAt),
    dateModified:
      data?.colorPages?.find(item => item.slug === data.slug)?.updatedAt ||
      (data?.hubs?.length && data.hubs[0].updatedAt),
    mainEntityOfPage: {
      [typeProp]: 'WebPage',
      [idProp]: `${mainUrl}/${data.slug}`,
    },
    publisher: {
      [typeProp]: 'Organization',
      name: 'Picsart',
      logo: {
        [typeProp]: 'ImageObject',
        url: 'https://pastatic.picsart.com/cms-pastatic/7c8adf15-a95b-4469-9682-abf44edb99bb.svg',
      },
    },
  };
};

export const useItemListSchema = (data: Record<string, any>) => {
  return {
    ...itemListSchema,
    itemListElement: data.map(item => ({
      [typeProp]: 'ListItem',
      position: item.position,
      name: item.anchor,
      ...(item.link ? { url: `${mainUrl}${item.link}` } : {}),
    })),
  };
};

export const useVideoObjectSchema = (data: Record<string, any>) => {
  const videoUrl = data?.components
    ?.map(
      component =>
        component?.content?.video?.src ||
        component?.content?.media?.videoSrc ||
        component?.content?.embed?.videoLink,
    )
    .find(item => item !== undefined);
  return {
    ...videoObjectSchema,
    name: data?.seoSettings?.title,
    description: data?.seoSettings?.metaDescription,
    thumbnailUrl: data?.imgSrc,
    uploadDate: data?.hubs?.length ? data.hubs[0].createdAt : undefined,
    contentUrl: videoUrl,
    embedUrl: videoUrl,
    publisher: {
      [typeProp]: 'Organization',
      name: 'Picsart',
      logo: {
        [typeProp]: 'ImageObject',
        url: 'https://pastatic.picsart.com/cms-pastatic/7c8adf15-a95b-4469-9682-abf44edb99bb.svg',
      },
    },
  };
};

const schemaTypeHandlerMap = {
  [SchemaType.FAQ]: useFAQSchema,
  [SchemaType.HowTo]: useHowToSchema,
  [SchemaType.RedesignedHowTo]: useRedesignedHowToSchema,
  [SchemaType.AggregateRating]: useRatingSchema,
  [SchemaType.WebPage]: useWebPageSchema,
  [SchemaType.Organization]: useOrganizationSchema,
  [SchemaType.Product]: useProductSchema,
  [SchemaType.Review]: useReviewSchema,
  [SchemaType.Breadcrumb]: useBreadcrumbsSchema,
  [SchemaType.Article]: useArticleSchema,
  [SchemaType.ItemList]: useItemListSchema,
  [SchemaType.VideoObject]: useVideoObjectSchema,
};

export const schemaDataMap = (data: Record<string, string | number>[] | Array<TemplateModel>) => {
  const arr = [];
  data.map(subData => {
    if (subData.id !== 123) {
      arr.push({
        name: subData.type,
        link: `/template/${buildSlugFromNameAndId({
          id: Number(subData?.id),
          name: String(subData?.display_name),
        })}`,
        type: subData.display_name,
        img: subData.url,
      });
    }
    return arr;
  });
  return arr;
};

const useCreateSchema = ({ data, type }: JSONLinkedDataProps) => {
  const dataToJson = () => {
    const result = schemaTypeHandlerMap[type];

    return result || (() => data);
  };

  return dataToJson();
};

export default useCreateSchema;
